import type { LoadUserSessionRequest } from "@oneapp/data";
import {
  resolveAinaTenantForBundle,
  resolveAinaExamPreferenceForLoadUserSession,
} from "@repo/utils";

import { useAuthStore } from "../../store/useAuthStore";

/**
 * Builds POST /session/load-user-session body for web (AINA client).
 * Mirrors mobile `buildLoadAinaUserSessionBody` with `platform: "WEB"`.
 */
export function buildLoadAinaUserSessionBody(overrides?: {
  examPreference?: string;
  agent?: string;
  latestExamId?: string | null;
  latestExamDetailsRes?: unknown;
}): LoadUserSessionRequest | null {
  const { profilesData, enrolledPrograms, user, token } =
    useAuthStore.getState();
  if (!token?.trim()) return null;

  const u = profilesData?.unified_account_details?.user_account_details;
  const userId =
    profilesData?.unified_account_details?.profiles?.[0]?.uam_id ?? "";

  const isd = u?.isd_code ? String(u.isd_code).replace(/^\+/, "") : "";
  const rawPhone = u?.phone ? String(u.phone).replace(/\D/g, "") : "";
  const studentPhone = rawPhone ? (isd ? `${isd}${rawPhone}` : rawPhone) : "";
  const studentName =
    [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim() ||
    user?.name ||
    "Student";
  const studentGrade =
    u?.grade?.name ??
    (u?.grade?.gradeId != null ? String(u.grade.gradeId) : "");
  const defaultProgram =
    enrolledPrograms?.find((p) => p.isDefault) ?? enrolledPrograms?.[0];
  const examPreference = resolveAinaExamPreferenceForLoadUserSession(
    defaultProgram,
    overrides,
  );

  const profiles = profilesData?.unified_account_details?.profiles;
  const profile = profiles?.find((p) => p.is_default_profile) ?? profiles?.[0];
  const uRecord = u as Record<string, unknown> | undefined;
  const userTenantRaw = uRecord?.tenant_id ?? uRecord?.tenantId;
  const userTenantId =
    typeof userTenantRaw === "string" || typeof userTenantRaw === "number"
      ? userTenantRaw
      : undefined;
  const tenant = resolveAinaTenantForBundle({
    userTenantId,
    programTenantId: defaultProgram?.tenant_id,
    profileTenantId: profile?.tenant_id,
  });

  return {
    agent: overrides?.agent?.trim() || "AINA_VOICE",
    user_info: {
      student_id: String(userId || ""),
      student_phone: studentPhone || "0000000000",
      student_name: studentName,
      student_grade: studentGrade || "—",
      exam_preference: examPreference || "—",
    },
    agent_config: {
      tenant,
      platform: "WEB",
    },
    force_new_session: false,
  };
}
